import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
	usePrefs,
	useLabels,
	useRequestData,
	useSitemap
} from 'wsm-common-data';
import { IncentiveModal } from 'wsm-incentive-modal';
import { setClassNames } from 'ddc-classnames-js';
import DetailsModal from './DetailsModal';
import { g, aR } from '../global-constants';
import { getPlacement } from '../utilities/aspect';
import { trackEvent } from '../utilities/tracking';
import AppleWalletButton from './AppleWalletButton';

const DetailsButton = ({
	btnSize,
	btnStyle,
	carouselPosition,
	customAsset = false,
	classes = [],
	incentive = {},
	applicableVehicle = '',
	isVisible = true,
	listingConfigId = '',
	promo,
	cta,
	fullLabel = false
}) => {
	const labels = useLabels();
	const sitemap = useSitemap();
	const { widgetName, windowId, deviceType } = useRequestData();
	const isMobile = deviceType === 'MOBILE';
	const account = useSelector((state) => state.account);
	const personalized = useSelector((state) => state.personalized);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const { aspectRatio, enableAppleWallet } = usePrefs();
	let applePassBtn = null;
	const isIphone =
		navigator.userAgent.includes('iPhone') &&
		navigator.userAgent.includes('AppleWebKit');

	const label =
		aspectRatio === aR.MOBILE_6X1 && !fullLabel
			? labels.get('DETAILS')
			: labels.get('OFFER_DETAILS_AND_DISCLAIMERS');
	let url = sitemap.get('PROMOTIONS_DETAILS_FRAGMENT');

	let button = null;
	let modal = null;

	const {
		id: promoId,
		type: promoType,
		title: promoTitle,
		additionalDetails: promoDetails,
		disclaimer: promoDisclaimer,
		description: promoDescription,
		discountValue: promoDiscountValue,
		discountType: promoDiscountType,
		endDateDisplay: promoEndDateDisplay
	} = promo;
	const showAppleWalletBtn = enableAppleWallet === 'true' && isIphone;

	if (showAppleWalletBtn) {
		applePassBtn = (
			<AppleWalletButton
				title={promoTitle}
				description={promoDescription}
				disclaimer={promoDisclaimer}
				discountValue={promoDiscountValue}
				discountType={promoDiscountType}
				endDateDisplay={promoEndDateDisplay}
				id={promoId}
				promoDetails={promoDetails}
				incentive={incentive}
				applicableVehicle={applicableVehicle}
			/>
		);
	}

	const showDetailsBtn =
		typeof promoDetails !== 'undefined' ||
		typeof promoDisclaimer !== 'undefined' ||
		Object.keys(incentive).length !== 0 ||
		(cta !== null && aspectRatio === aR.MOBILE_6X1) ||
		showAppleWalletBtn;

	// Only display the button if we have a page fragment to link it to
	if ((url && showDetailsBtn) || promo.couponCode) {
		url = `${url}?promotionId=${promoId}&promotionType=${promoType}&showDetailsPromotion=false`;
		url = listingConfigId
			? `${url}&listingConfigId=${listingConfigId}`
			: url;

		if (isModalVisible && promoType === g.INCENTIVE) {
			modal = (
				<IncentiveModal
					account={account}
					incentive={incentive}
					isOpen={isModalVisible}
					isMobile={isMobile}
					onModalClose={() => setIsModalVisible(false)}
					cta={cta}
					applePassBtn={applePassBtn}
				/>
			);
		} else if (isModalVisible) {
			modal = (
				<DetailsModal
					isOpen={isModalVisible}
					isMobile={isMobile}
					onModalClose={() => setIsModalVisible(false)}
					promo={promo}
					cta={cta}
					applePassBtn={applePassBtn}
				/>
			);
		}

		const onButtonClick = () => {
			setIsModalVisible(true);
			trackEvent({
				widgetName,
				windowId,
				event: 'specialsEvent',
				specialType: 'disclaimer click',
				specialCategory:
					promoType.charAt(0) + promoType.slice(1).toLowerCase(),
				specialId: promoId,
				specialPlacementType: getPlacement(aspectRatio),
				specialPersonalized: personalized,
				specialAutoGen: promo.autoGenerated,
				nonInteractive: false,
				carouselPosition,
				customAsset
			});
		};

		// data-promo-tracking-label="dialog : inventory query - [days on lot gt 100, body style eq sedan]"
		// Set tabindex='-1' to hide offscreen Button from tabbing for A11Y
		button = (
			<React.Fragment>
				<Button
					bsStyle={btnStyle}
					bsSize={btnSize}
					className={setClassNames([
						!customAsset ? 'btn-inverse' : '',
						...classes
					])}
					data-width="670"
					data-title={label}
					data-href={url}
					data-promo-tracking-label="dialog"
					data-promo-tracking-action="click"
					data-promo-tracking-description="disclaimer details"
					href="#"
					onClick={() => onButtonClick()}
					tabIndex={isVisible ? null : '-1'}
					rel="nofollow"
				>
					{label}
				</Button>
				{modal}
			</React.Fragment>
		);
	}
	return button;
};

DetailsButton.propTypes = {
	btnSize: PropTypes.string,
	btnStyle: PropTypes.string,
	carouselPosition: PropTypes.number.isRequired,
	classes: PropTypes.arrayOf(PropTypes.string),
	customAsset: PropTypes.bool,
	incentive: PropTypes.shape({}),
	fullLabel: PropTypes.bool,
	isVisible: PropTypes.bool,
	listingConfigId: PropTypes.string,
	promo: PropTypes.shape({}).isRequired
};

export default DetailsButton;
